import { useAvContext } from "@minervaproject/avsubsystem";
import { SettingsIcon } from "@minervaproject/fds";
import { capitalize } from "lodash";
import { useContext, useEffect, useState } from "react";

import { Menu } from "./menu";
import { MenuItem } from "./menu_item";
import { MenuSeparator } from "./menu_separator";

import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useHasClassStarted } from "hooks/use_has_class_started";
import { useUserProperties } from "hooks/use_user_properties";
import {
  clearAllStagePanes,
  muteAllExceptInstructor,
  sendPubsubMessage,
  setAvService,
  setBandwidthModeForUser,
  setClassType,
  setIsInFlight,
  setMaxSubscribedStreams,
  setPttModeForClass,
  setStagePane,
  setUserEnableLiveCaptions,
  setUserIsPerformingAvCheck,
} from "shared/reducers/classroom";
import {
  AvServiceType,
  ClassTypeType,
  LocalModalContentType,
  PageUnloadReasonType,
  PaneContentType,
  PubsubMessageTypeType,
} from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { BandwidthModeType } from "shared/types/user";
import { getPaneIndexForUser } from "shared/utils/stage";
import { classroomSelectors } from "store";
import { showLocalModal } from "store/reducers/local";
import { createMonitoringEvent } from "store/reducers/monitoring";

function SettingsMenu({
  userId,
  canBecomeTeacher,
  canControlClassroom,
  isAvEnabled,
  isVisibleInClass,
  isInPerson,
}: {
  userId: string;
  canBecomeTeacher: boolean;
  canControlClassroom: boolean;
  isAvEnabled: boolean;
  isVisibleInClass: boolean;
  isInPerson: boolean;
}) {
  const dispatch = useAppDispatch();
  const forumAPI = useContext(ForumAPIContext);
  const classId = useAppSelector(classroomSelectors.selectClassId());
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const hasClassStarted = useHasClassStarted(classInfo);
  const teacherUserId = useAppSelector(classroomSelectors.selectUserIdForTeacher());
  const avService = useAppSelector(classroomSelectors.selectAvService());
  const canResetSession = useAppSelector(classroomSelectors.selectCurrentUserCanResetSession());
  const canSwitchToAudioOnly =
    useAppSelector(classroomSelectors.selectEnableStudentAudioOnly()) || canControlClassroom;
  const user = useUserProperties(userId, ["bandwidth_mode", "enable_live_captions"]);
  const { bandwidth_mode = BandwidthModeType.AutomaticBandwidth, enable_live_captions } =
    user || {};
  const { panes } = useAppSelector(classroomSelectors.selectStage());
  const [isWaitingToBecomeInstructor, setIsWaitingToBecomeInstructor] = useState(false);
  const [oldTeacherUserId, setOldTeacherUserId] = useState("");

  const availableBandwidthModes = (
    Object.keys(BandwidthModeType) as (keyof typeof BandwidthModeType)[]
  ).filter((key) => {
    return (
      BandwidthModeType[key] !== bandwidth_mode &&
      (canSwitchToAudioOnly || key !== "AudioOnly") &&
      key !== "AudioOnlyForced"
    );
  });

  const avConnection = useAvContext();
  const avServices = [AvServiceType.Licode, AvServiceType.Opentok, AvServiceType.Daily].filter(
    (_avService) => _avService !== avService,
  );
  const isClassInPttMode = useAppSelector(classroomSelectors.selectPttModeForClass());

  const handleShowAvPreferences = () => {
    dispatch(setUserIsPerformingAvCheck({ userId, isPerformingAvCheck: true }));
    dispatch(showLocalModal({ content: LocalModalContentType.AvPreferences }));
  };

  const handleRefreshAv = () => {
    avConnection?.refreshAv();
  };

  const handleClearStage = () => {
    dispatch(clearAllStagePanes({}));
  };

  const handleMuteAllExceptInstructor = () => {
    dispatch(muteAllExceptInstructor());
  };

  const handleSoftReset = async () => {
    if (
      confirm(
        "Soft reset this class? This will reset the timeline, stage configuration, all breakouts, " +
          "and all user signals and AV connections. Then, EVERYBODY IN CLASS will be forced to reload the page.",
      )
    ) {
      const response = await forumAPI.softReset(classId);
      if (!response.ok) {
        window.flightLogger.error(`softReset failed with status=${response.status}`);
      }
    }
  };

  const handleHardReset = async () => {
    if (
      confirm(
        "Hard reset this class? " +
          "This will reset the timeline, stage configuration, breakouts and groups, " +
          "user signals and AV connections, polls, resources, chat, presence indicators, " +
          "and class events. Then, EVERYBODY IN CLASS will be forced to reload the page.",
      )
    ) {
      const response = await forumAPI.hardReset(classId);
      if (!response.ok) {
        window.flightLogger.error(`hardReset failed with status=${response.status}`);
      }
    }
  };

  const handleReloadPage = () => {
    if (confirm("Force reload the page for EVERYBODY IN CLASS? (except you)")) {
      dispatch(
        sendPubsubMessage({
          type: PubsubMessageTypeType.RELOAD_PAGE,
          data: { reason: PageUnloadReasonType.FORCE_RELOAD_PAGE_EVERYBODY },
        }),
      );
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.ADMIN_RELOAD_PAGE_FOR_EVERYBODY,
          contextData: { targetUserId: null },
          userId,
        }),
      );
    }
  };

  const handleSwitchAvService = (newAvService: AvServiceType) => () => {
    if (confirm(`Switch to ${capitalize(newAvService)} and refresh A/V for EVERYBODY IN CLASS?`)) {
      dispatch(setAvService({ avService: newAvService }));
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.ADMIN_CHANGE_AV_SERVICE,
          contextData: {
            avService: newAvService,
            avState: avConnection?.getAvStateSnapshot() || undefined,
          },
          userId,
        }),
      );
    }
  };

  const handleSwitchBandwidthMode = (newBandwidthMode: BandwidthModeType) => () => {
    dispatch(setBandwidthModeForUser({ userId, bandwidthMode: newBandwidthMode }));
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.CHANGE_BANDWIDTH_MODE,
        userId,
        contextData: {
          bandwidthMode: newBandwidthMode,
          avState: avConnection?.getAvStateSnapshot() || undefined,
        },
      }),
    );
  };

  const handleToggleLiveCaptions = () => {
    const enableLiveCaptions = !enable_live_captions;
    dispatch(setUserEnableLiveCaptions({ userId, enableLiveCaptions }));
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.TOGGLE_LIVE_CAPTIONS,
        userId,
        contextData: { enableLiveCaptions },
      }),
    );
  };

  const handleSwitchClassType = async () => {
    const newClassType = isInPerson ? ClassTypeType.Virtual : ClassTypeType.InPerson;
    if (
      confirm(
        `Switch to ${
          newClassType === ClassTypeType.InPerson ? "an In-Person Class" : "a Virtual Class"
        }? This may reload the page for EVERYBODY IN CLASS.`,
      )
    ) {
      dispatch(
        createMonitoringEvent({
          type: MonitoringEventTypeType.CHANGE_CLASS_MODALITY,
          userId,
          contextData: { modality: newClassType },
        }),
      );
      const response = await forumAPI.changeModality(classId, newClassType);
      const responseJson = await response.json();

      if (response.ok) {
        if (responseJson.is_in_flight) {
          dispatch(setClassType({ classType: newClassType }));
          dispatch(
            setMaxSubscribedStreams({ maxSubscribedStreams: responseJson.max_subscribed_streams }),
          );
        } else {
          dispatch(setIsInFlight({ isInFlight: false }));
        }
      } else {
        window.flightLogger.error(`changeModality failed with status=${response.status}`);
      }
    }
  };

  const handleSwitchPttModeForClass = (pttMode: boolean) => {
    dispatch(setPttModeForClass({ pttMode }));
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.CHANGE_CLASS_PTT_MODE,
        userId,
        contextData: { ptt: pttMode },
      }),
    );
  };

  const handleBecomeTeacher = async () => {
    if (
      canBecomeTeacher &&
      confirm(
        "Are you sure you want to become the instructor of this class? " +
          "The instructor's role will change to Assistant.",
      )
    ) {
      setIsWaitingToBecomeInstructor(true);
      setOldTeacherUserId(teacherUserId);
      await forumAPI.becomeTeacher(classId, userId);
    }
  };

  useEffect(() => {
    if (isWaitingToBecomeInstructor && teacherUserId === userId) {
      const oldTeacherPaneIndex = getPaneIndexForUser(panes, oldTeacherUserId);
      if (oldTeacherPaneIndex > -1) {
        dispatch(
          setStagePane({
            index: oldTeacherPaneIndex,
            contentId: teacherUserId,
            contentType: PaneContentType.User,
          }),
        );
        setIsWaitingToBecomeInstructor(false);
        setOldTeacherUserId("");
      }
    }
  }, [dispatch, isWaitingToBecomeInstructor, oldTeacherUserId, panes, teacherUserId, userId]);

  if (!user) {
    return null;
  }

  return (
    <Menu tooltip="Settings" IconComponent={SettingsIcon} ariaLabel="Settings Menu">
      {canControlClassroom && isAvEnabled && !isInPerson && (
        <>
          <MenuItem
            text="Students Muted Until [CTRL] Pressed"
            onClick={() => handleSwitchPttModeForClass(true)}
            isSelected={isClassInPttMode}
          />
          <MenuItem
            text="Students Can Unmute at Any Time"
            onClick={() => handleSwitchPttModeForClass(false)}
            isSelected={!isClassInPttMode}
          />
          <MenuSeparator />
        </>
      )}
      {isAvEnabled &&
        isVisibleInClass &&
        availableBandwidthModes.map((_bandwidthMode) => (
          <MenuItem
            key={_bandwidthMode}
            // Add spaces between words to make it more readable
            text={`Switch to ${_bandwidthMode.replace(/([A-Z])/g, " $1").trim()}`}
            onClick={handleSwitchBandwidthMode(BandwidthModeType[_bandwidthMode])}
          />
        ))}

      {isAvEnabled && isVisibleInClass && availableBandwidthModes.length > 0 && <MenuSeparator />}

      {canBecomeTeacher && (
        <>
          <MenuItem text="Become Instructor" onClick={handleBecomeTeacher} />
          <MenuSeparator />
        </>
      )}
      {isAvEnabled && isVisibleInClass && (
        <MenuItem text="Audio & Video Preferences" onClick={handleShowAvPreferences} />
      )}
      {isAvEnabled && <MenuItem text="Refresh AV" onClick={handleRefreshAv} />}
      {canControlClassroom && (
        <>
          <MenuItem text="Clear Stage" onClick={handleClearStage} />
          <MenuItem text="Mute All Except Instructor" onClick={handleMuteAllExceptInstructor} />
        </>
      )}
      {canResetSession && (
        <>
          <MenuItem text="Soft Reset Class Session" onClick={handleSoftReset} />
          {!hasClassStarted && (
            <MenuItem text="Hard Reset Class Session" onClick={handleHardReset} />
          )}
        </>
      )}
      {canControlClassroom && (
        <MenuItem text="Reload Page for Everybody" onClick={handleReloadPage} />
      )}
      {canControlClassroom &&
        isAvEnabled &&
        avServices.map((_avService) => (
          <MenuItem
            key={_avService}
            text={`Switch to ${capitalize(_avService)}`}
            onClick={handleSwitchAvService(_avService)}
          />
        ))}

      {(isAvEnabled || canControlClassroom || canResetSession) && <MenuSeparator />}

      <MenuItem
        text="Show Captions"
        onClick={handleToggleLiveCaptions}
        isSelected={enable_live_captions}
      />

      {canControlClassroom && (
        <>
          <MenuSeparator />
          <MenuItem
            text={`Switch to ${isInPerson ? "Virtual Class" : "In-Person Class"}`}
            onClick={handleSwitchClassType}
          />
        </>
      )}
    </Menu>
  );
}

export { SettingsMenu };
